import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Calendar as CalendarIcon, Settings, Plus, Trash2, RefreshCw } from 'lucide-react';
import { adminAPI, API } from './AdminUtils';
import axios from 'axios';
import { cn } from '../../lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { useToast } from '@/hooks/use-toast';


const WEEK_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const WEEK_LABELS = {
  mon: 'Lundi',
  tue: 'Mardi',
  wed: 'Mercredi',
  thu: 'Jeudi',
  fri: 'Vendredi',
  sat: 'Samedi',
  sun: 'Dimanche',
};

const ModernCalendarTab = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [month, setMonth] = useState(() => new Date().getMonth() + 1);
  const [slots, setSlots] = useState({ month: '', days: {} });
  const [bookings, setBookings] = useState({ month: '', days: {} });
  const [dayReservations, setDayReservations] = useState({ date: '', reservations: [], total: 0 });
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [overrideDay, setOverrideDay] = useState('');
  const [overrideWindows, setOverrideWindows] = useState([]);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [selectedDayKey, setSelectedDayKey] = useState('');
  const [viewMode, setViewMode] = useState('weekly');
  const [weeks, setWeeks] = useState([]);
  const [weekIndex, setWeekIndex] = useState(0);
  const [reservationModalOpen, setReservationModalOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const { toast } = useToast();

  const loadConfig = async () => {
    try {
      const res = await adminAPI.get(`/calendar/config`);
      setCfg(res.data);
    } catch {
      setCfg({
        id: 'global',
        enabled: false,
        eventEnabled: false,
        slotDurationMinutes: 30,
        perWeekdayShifts: WEEK_KEYS.reduce((acc, k) => (acc[k] = [], acc), {}),
        monthlyOverrides: {},
      });
    }
  };

  const loadSlots = async (y = year, m = month) => {
    setLoadingSlots(true);
    try {
      const res = await axios.get(`${API}/calendar/slots`, { params: { year: y, month: m } });
      setSlots(res.data || { month: '', days: {} });
      try {
        const bk = await adminAPI.get(`/calendar/bookings`, { params: { year: y, month: m } });
        setBookings(bk.data || { month: '', days: {} });
      } catch {
        setBookings({ month: '', days: {} });
      }
    } catch {
      setSlots({ month: '', days: {} });
    } finally {
      setLoadingSlots(false);
    }
  };

  useEffect(() => { loadConfig(); }, []);
  useEffect(() => { loadSlots(year, month); }, [year, month]);
  useEffect(() => {
    try {
      const first = new Date(year, month - 1, 1);
      const last = new Date(year, month, 0);
      const weeksArr = [];
      let week = [];
      for (let d = 1; d <= last.getDate(); d++) {
        const dateObj = new Date(year, month - 1, d);
        const wd = dateObj.getDay(); // 0=Sun..6=Sat
        const dk = String(d).padStart(2, '0');
        if (week.length === 0 && wd !== 1) {
          // align to Monday start: if first day not Monday, start week anyway
        }
        week.push(dk);
        if (wd === 0 || d === last.getDate()) { // end week on Sunday or last day
          weeksArr.push(week);
          week = [];
        }
      }
      setWeeks(weeksArr);
      const today = new Date();
      if (today.getFullYear() === year && (today.getMonth() + 1) === month) {
        const td = String(today.getDate()).padStart(2, '0');
        const idx = Math.max(0, weeksArr.findIndex(w => w.includes(td)));
        setWeekIndex(idx === -1 ? 0 : idx);
      } else {
        setWeekIndex(0);
      }
    } catch {
      setWeeks([]);
      setWeekIndex(0);
    }
  }, [year, month]);

  useEffect(() => {
    if (viewMode === 'daily') {
      const hasDay = !!(slots.days || {})[selectedDayKey];
      if (!selectedDayKey || !hasDay) {
        const today = new Date();
        if (today.getFullYear() === year && (today.getMonth() + 1) === month) {
          setSelectedDayKey(String(today.getDate()).padStart(2, '0'));
        } else {
          setSelectedDayKey('01');
        }
      }
    }
  }, [viewMode, year, month, selectedDayKey, slots]);

  const navigatePrev = async () => {
    if (viewMode === 'daily') {
      const keys = Object.keys(slots.days || {}).sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
      const idx = Math.max(0, keys.findIndex(k => k === selectedDayKey));
      if (idx > 0) {
        setSelectedDayKey(keys[idx - 1]);
      } else {
        let ny = year, nm = month - 1;
        if (nm < 1) { nm = 12; ny = year - 1; }
        setYear(ny); setMonth(nm);
        const lastDay = new Date(ny, nm, 0).getDate();
        setSelectedDayKey(String(lastDay).padStart(2, '0'));
      }
    } else if (viewMode === 'weekly') {
      if (weekIndex > 0) {
        setWeekIndex(weekIndex - 1);
      } else {
        let ny = year, nm = month - 1;
        if (nm < 1) { nm = 12; ny = year - 1; }
        setYear(ny); setMonth(nm);
        // weeks will recompute; pick last week afterwards
        setTimeout(() => {
          setWeekIndex(Math.max(0, (weeks.length || 1) - 1));
        }, 0);
      }
    } else {
      let ny = year, nm = month - 1;
      if (nm < 1) { nm = 12; ny = year - 1; }
      setYear(ny); setMonth(nm);
    }
  };
  const navigateNext = async () => {
    if (viewMode === 'daily') {
      const keys = Object.keys(slots.days || {}).sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
      const idx = Math.max(0, keys.findIndex(k => k === selectedDayKey));
      if (idx < keys.length - 1) {
        setSelectedDayKey(keys[idx + 1]);
      } else {
        let ny = year, nm = month + 1;
        if (nm > 12) { nm = 1; ny = year + 1; }
        setYear(ny); setMonth(nm);
        setSelectedDayKey('01');
      }
    } else if (viewMode === 'weekly') {
      if (weekIndex < Math.max(0, weeks.length - 1)) {
        setWeekIndex(weekIndex + 1);
      } else {
        let ny = year, nm = month + 1;
        if (nm > 12) { nm = 1; ny = year + 1; }
        setYear(ny); setMonth(nm);
        setWeekIndex(0);
      }
    } else {
      let ny = year, nm = month + 1;
      if (nm > 12) { nm = 1; ny = year + 1; }
      setYear(ny); setMonth(nm);
    }
  };

  const addWeekWindow = (wk) => {
    const next = { ...(cfg || {}) };
    next.perWeekdayShifts = { ...(cfg?.perWeekdayShifts || {}) };
    const list = [...(next.perWeekdayShifts[wk] || [])];
    list.push({ start: '18:00', end: '22:00' });
    next.perWeekdayShifts[wk] = list;
    setCfg(next);
  };
  const updateWeekWindow = (wk, idx, field, value) => {
    const next = { ...(cfg || {}) };
    next.perWeekdayShifts = { ...(cfg?.perWeekdayShifts || {}) };
    const list = [...(next.perWeekdayShifts[wk] || [])];
    list[idx] = { ...(list[idx] || {}), [field]: value };
    next.perWeekdayShifts[wk] = list;
    setCfg(next);
  };
  const deleteWeekWindow = (wk, idx) => {
    const next = { ...(cfg || {}) };
    next.perWeekdayShifts = { ...(cfg?.perWeekdayShifts || {}) };
    const list = [...(next.perWeekdayShifts[wk] || [])];
    list.splice(idx, 1);
    next.perWeekdayShifts[wk] = list;
    setCfg(next);
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const payload = { ...(cfg || {}) };
      await adminAPI.put(`/calendar/config`, payload);
      await loadConfig();
      toast({ title: 'Calendrier enregistré', description: 'Paramètres sauvegardés avec succès.' });
    } catch {
      toast({ title: 'Erreur de sauvegarde', description: 'Vérifiez les heures (HH:MM) et que le début précède la fin.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const MONTH_LABELS = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
  const monthOptions = useMemo(() => Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: MONTH_LABELS[i] })), []);
  const yearOptions = useMemo(() => {
    const y = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => y - 1 + i);
  }, []);

  const openOverride = (dayKey) => {
    setOverrideDay(dayKey);
    const ext = (cfg?.monthlyOverrides || {})[`${year.toString().padStart(4,'0')}-${month.toString().padStart(2,'0')}`] || {};
    const wins = ext[`${parseInt(dayKey, 10)}`] || [];
    setOverrideWindows(wins.map(w => ({ start: w.start, end: w.end })));
    setOverrideOpen(true);
    const yyyy = year.toString().padStart(4, '0');
    const mm = month.toString().padStart(2, '0');
    const dd = String(parseInt(dayKey, 10)).padStart(2, '0');
    (async () => {
      try {
        const res = await adminAPI.get(`/calendar/bookings/day`, { params: { date_str: `${yyyy}-${mm}-${dd}` } });
        setDayReservations({ date: `${dd}/${mm}`, reservations: res.data.reservations || [], total: res.data.total || 0 });
      } catch {
        setDayReservations({ date: `${dd}/${mm}`, reservations: [], total: 0 });
      }
    })();
  };
  const openDayDetail = (dayKey) => {
    setSelectedDayKey(dayKey);
    setViewMode('daily');
    const yyyy = year.toString().padStart(4, '0');
    const mm = month.toString().padStart(2, '0');
    const dd = String(parseInt(dayKey, 10)).padStart(2, '0');
    (async () => {
      try {
        const res = await adminAPI.get(`/calendar/bookings/day`, { params: { date_str: `${yyyy}-${mm}-${dd}` } });
        setDayReservations({ date: `${dd}/${mm}`, reservations: res.data.reservations || [], total: res.data.total || 0 });
      } catch {
        setDayReservations({ date: `${dd}/${mm}`, reservations: [], total: 0 });
      }
    })();
  };
  const addOverrideWindow = () => {
    setOverrideWindows(prev => [...prev, { start: '18:00', end: '22:00' }]);
  };
  const updateOverrideWindow = (idx, field, value) => {
    setOverrideWindows(prev => prev.map((w, i) => i === idx ? { ...w, [field]: value } : w));
  };
  const deleteOverrideWindow = (idx) => {
    setOverrideWindows(prev => prev.filter((_, i) => i !== idx));
  };
  const saveOverride = async () => {
    const monthKey = `${year.toString().padStart(4,'0')}-${month.toString().padStart(2,'0')}`;
    const next = { ...(cfg || {}) };
    next.monthlyOverrides = { ...(cfg?.monthlyOverrides || {}) };
    const ext = { ...(next.monthlyOverrides[monthKey] || {}) };
    ext[`${parseInt(overrideDay, 10)}`] = overrideWindows.map(w => ({ start: w.start, end: w.end }));
    next.monthlyOverrides[monthKey] = ext;
    setCfg(next);
    try {
      await adminAPI.put(`/calendar/config`, next);
      await loadSlots(year, month);
      setOverrideDay('');
      setOverrideWindows([]);
      setOverrideOpen(false);
      toast({ title: 'Tranches enregistrées', description: `Jour ${overrideDay}/${String(month).padStart(2,'0')}` });
    } catch (e) {
      const msg = (e?.response?.data?.detail) || 'Erreur lors de l’enregistrement des tranches. Assurez-vous que les heures soient valides (HH:MM) et que le début précède la fin.';
      toast({ title: 'Erreur de tranches', description: msg, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Calendrier des créneaux</h2>
          <p className="text-slate-400">Gérer les créneaux horaires par jour et par mois</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => { loadConfig(); loadSlots(year, month); }}
            className="text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            <RefreshCw size={16} className={saving || loadingSlots ? 'animate-spin' : ''} />
            <span className="ml-2">Actualiser</span>
          </Button>
        </div>
      </div>

      <Tabs defaultValue="calendar" className="w-full">
        <TabsList className="border bg-slate-800/40 border-slate-700">
          <TabsTrigger value="calendar">Calendrier</TabsTrigger>
          <TabsTrigger value="settings">Paramètres</TabsTrigger>
        </TabsList>
        <TabsContent value="settings">
      <Card className="bg-slate-900/40 border-slate-700">
        <CardHeader>
          <CardTitle className="flex gap-2 items-center">
            <Settings size={18} />
            Paramètres
          </CardTitle>
          <CardDescription className="text-slate-400">Activer les créneaux et définir les tranches horaires</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 rounded border bg-slate-800/40 border-slate-700">
                <div>
                  <div className="text-sm font-semibold text-white">Activer la sélection de créneaux</div>
                  <div className="text-xs text-slate-400">Permettre aux réservations de choisir une date et une heure</div>
                </div>
                <Switch checked={!!cfg?.enabled} onCheckedChange={(v) => setCfg(prev => ({ ...(prev || {}), enabled: !!v }))} />
              </div>
              <div className="flex justify-between items-center p-3 rounded border bg-slate-800/40 border-slate-700">
                <div>
                  <div className="text-sm font-semibold text-white">Activer les créneaux pour les événements</div>
                  <div className="text-xs text-slate-400">Permettre un choix d’heure le jour de l’événement</div>
                </div>
                <Switch checked={!!cfg?.eventEnabled} onCheckedChange={(v) => setCfg(prev => ({ ...(prev || {}), eventEnabled: !!v }))} />
              </div>
              <div>
                <Label>Durée d’un créneau (minutes)</Label>
                <Input
                  type="number"
                  min="5"
                  max="240"
                  value={cfg?.slotDurationMinutes ?? 30}
                  onChange={(e) => setCfg(prev => ({ ...(prev || {}), slotDurationMinutes: Math.max(5, Math.min(240, parseInt(e.target.value || '30'))) }))}
                  className="text-white bg-slate-900 border-slate-700"
                />
              </div>
            </div>
            <div className="space-y-4">
              {WEEK_KEYS.map(wk => (
                <div key={wk} className="p-3 rounded border bg-slate-800/40 border-slate-700">
                  <div className="flex justify-between items-center mb-2">
                    <div className="text-sm font-semibold text-white">{WEEK_LABELS[wk]}</div>
                    <Button size="sm" onClick={() => addWeekWindow(wk)} className="text-white bg-slate-700 hover:bg-slate-600">
                      <Plus size={14} className="mr-1" /> Ajouter une tranche
                    </Button>
                  </div>
                  {(cfg?.perWeekdayShifts?.[wk] || []).length ? (
                    <div className="space-y-2">
                      {(cfg?.perWeekdayShifts?.[wk] || []).map((w, idx) => (
                        <div key={idx} className="grid grid-cols-3 gap-2">
                          <Input type="time" step="60" min="00:00" max="23:59" value={w.start || ''} onChange={(e) => updateWeekWindow(wk, idx, 'start', e.target.value)} className="w-full text-white bg-slate-900 border-slate-700" />
                          <Input type="time" step="60" min="00:00" max="23:59" value={w.end || ''} onChange={(e) => updateWeekWindow(wk, idx, 'end', e.target.value)} className="w-full text-white bg-slate-900 border-slate-700" />
                          <Button variant="destructive" size="sm" onClick={() => deleteWeekWindow(wk, idx)}><Trash2 size={14} /></Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-slate-400">Aucune tranche définie</div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-2 justify-end mt-4">
            <Button onClick={saveConfig} disabled={saving} className="text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
              Enregistrer
            </Button>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="calendar">
      <Card className="bg-slate-900/40 border-slate-700">
        <CardHeader>
          <CardTitle className="flex gap-2 items-center">
            <CalendarIcon size={18} />
            Calendrier
          </CardTitle>
          <CardDescription className="text-slate-400">Voir les créneaux et réservations</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div>
              <Label>Vue</Label>
              <Select value={String(viewMode)} onValueChange={(v) => setViewMode(v)}>
                <SelectTrigger className="text-white bg-slate-900 border-slate-700"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="daily" className="text-white">Quotidienne</SelectItem>
                  <SelectItem value="weekly" className="text-white">Hebdomadaire</SelectItem>
                  <SelectItem value="monthly" className="text-white">Mensuelle</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Mois</Label>
              <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
                <SelectTrigger className="text-white bg-slate-900 border-slate-700"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {monthOptions.map(m => (
                    <SelectItem key={m.value} value={String(m.value)} className="text-white">{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Semaine</Label>
              <Select value={String(weekIndex)} onValueChange={(v) => setWeekIndex(parseInt(v, 10))}>
                <SelectTrigger className="text-white bg-slate-900 border-slate-700"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {weeks.map((w, i) => (
                    <SelectItem key={i} value={String(i)} className="text-white">Semaine {i + 1}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Année</Label>
              <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
                <SelectTrigger className="text-white bg-slate-900 border-slate-700"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {yearOptions.map(y => (
                    <SelectItem key={y} value={String(y)} className="text-white">{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={() => loadSlots(year, month)} className="text-white bg-slate-700 hover:bg-slate-600">Charger</Button>
            </div>
          </div>
          {viewMode !== 'daily' && (
          <div className="flex gap-2 justify-end items-center mt-2">
            <Button variant="outline" className="text-slate-300 border-slate-600 hover:bg-slate-700 hover:text-white" onClick={navigatePrev}>Précédent</Button>
            <Button className="text-white bg-slate-700 hover:bg-slate-600" onClick={navigateNext}>Suivant</Button>
          </div>
          )}

          {viewMode !== 'daily' && (
          <div className="overflow-x-auto mt-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Jour (dd/mm)</TableHead>
                  <TableHead>Tranches</TableHead>
                  <TableHead>Réservations</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(viewMode === 'weekly' ? weeks[weekIndex] || [] : Object.keys(slots.days || {}))
                  .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
                  .map((dk) => {
                  const day = slots.days[dk] || {};
                  const ddmm = `${dk}/${String(month).padStart(2,'0')}`;
                  const DAY_LABELS = ['Dimanche','Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi'];
                  const dateObj = new Date(year, month - 1, parseInt(dk, 10));
                  const dayName = (day.weekdayLabel) || DAY_LABELS[dateObj.getDay()];
                  const count = (bookings.days || {})[dk] || 0;
                  const isClosed = !!day.closed;
                  const today = new Date();
                  const isThisMonthYear = (today.getFullYear() === year) && ((today.getMonth() + 1) === month);
                  const isToday = isThisMonthYear && (String(today.getDate()).padStart(2,'0') === dk);
                  return (
                    <TableRow key={dk} className={cn("cursor-pointer hover:bg-slate-800/40", isToday && "bg-blue-500/10")} onClick={() => openDayDetail(dk)}>
                      <TableCell className={cn("text-white", isToday && "font-semibold")}>{dayName} — {ddmm}</TableCell>
                      <TableCell className="text-slate-300">
                        {(day.windows || []).map((w, i) => <span key={i} className="inline-block px-2 py-1 mr-1 text-xs rounded border bg-slate-800 border-slate-700">{w.start} - {w.end}</span>)}
                      </TableCell>
                      <TableCell className="text-slate-300">
                        <span className={cn("inline-flex px-2 py-1 text-xs rounded border", count > 0 ? "bg-green-500/10 border-green-500/40 text-green-400" : "bg-slate-800 border-slate-700 text-slate-300")}>
                          {count} réservation(s)
                        </span>
                        {isClosed && <span className="inline-block px-2 py-1 ml-2 text-xs text-red-400 rounded border bg-red-500/10 border-red-500/40">Complet</span>}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button size="sm" className="text-white bg-slate-700 hover:bg-slate-600" onClick={(e) => { e.stopPropagation(); openOverride(dk); }}>Éditer</Button>
                          <Button
                            size="sm"
                            variant={isClosed ? "outline" : "destructive"}
                            className={cn(isClosed ? "border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white" : "")}
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await adminAPI.post(`/calendar/day/closed`, null, { params: { year, month, day: parseInt(dk, 10), closed: !isClosed } });
                                await loadSlots(year, month);
                                toast({ title: isClosed ? 'Jour réouvert' : 'Jour marqué complet', description: `${ddmm}` });
                              } catch {
                                toast({ title: 'Erreur statut jour', description: 'Impossible de mettre à jour ce jour.', variant: 'destructive' });
                              }
                            }}
                          >
                            {isClosed ? "Réouvrir" : "Marquer complet"}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          )}

          {viewMode === 'daily' && selectedDayKey && (
            <div className="mt-4">
              <div className="flex justify-between items-center mb-4">
                <div className="text-lg font-semibold text-white">
                  {(() => {
                    const DAY_LABELS = ['Dimanche','Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi'];
                    const dateObj = new Date(year, month - 1, parseInt(selectedDayKey, 10));
                    const dayName = (slots.days?.[selectedDayKey]?.weekdayLabel) || DAY_LABELS[dateObj.getDay()];
                    return `${dayName} — ${selectedDayKey}/${String(month).padStart(2,'0')}/${year}`;
                  })()}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="text-slate-300 border-slate-600 hover:bg-slate-700 hover:text-white" onClick={navigatePrev}>Précédent</Button>
                  <Button className="text-white bg-slate-700 hover:bg-slate-600" onClick={navigateNext}>Suivant</Button>
                  <Button className="text-white bg-slate-700 hover:bg-slate-600" onClick={() => openOverride(selectedDayKey)}>Éditer les tranches</Button>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="p-4 rounded border bg-slate-800/40 border-slate-700">
                  <div className="text-xs tracking-wide uppercase text-slate-400">Tranches (générales)</div>
                  <div className="mt-2">
                    {(slots.days?.[selectedDayKey]?.windows || []).map((w, i) => (
                      <span key={i} className="inline-block px-2 py-1 mr-1 text-xs rounded border bg-slate-800 border-slate-700">{w.start} - {w.end}</span>
                    ))}
                  </div>
                </div>
                <div className="p-4 rounded border bg-slate-800/40 border-slate-700 md:col-span-2">
                  <div className="text-xs tracking-wide uppercase text-slate-400">Créneaux</div>
                  <div className="mt-2">
                    {(slots.days?.[selectedDayKey]?.slots || []).map((s, i) => (
                      <span key={i} className="inline-block px-2 py-1 mr-1 text-xs rounded border bg-slate-800 border-slate-700">{s}</span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="p-4 mt-4 rounded border bg-slate-800/40 border-slate-700">
                <div className="flex justify-between items-center">
                  <div className="text-xs tracking-wide uppercase text-slate-400">Réservations ({dayReservations.total})</div>
                  <Button size="sm" className="text-white bg-slate-700 hover:bg-slate-600" onClick={() => openDayDetail(selectedDayKey)}>Actualiser</Button>
                </div>
                <div className="mt-2 space-y-2">
                  {(dayReservations.reservations || []).map((r) => (
                    <div
                      key={r.id}
                      className="p-3 rounded border cursor-pointer bg-slate-900 border-slate-700 hover:bg-slate-800/50"
                      onClick={() => { setSelectedReservation(r); setReservationModalOpen(true); }}
                    >
                      <div className="flex gap-2 items-center">
                        <span className="font-semibold text-white">{r.name}</span>
                        <span className="text-slate-400">({r.guests} pers.)</span>
                        {(() => {
                          const st = r.status || 'pending';
                          const klass = st === 'confirmed'
                            ? "bg-green-500/10 border-green-500/40 text-green-400"
                            : st === 'cancelled'
                              ? "bg-red-500/10 border-red-500/40 text-red-400"
                              : "bg-yellow-500/10 border-yellow-500/40 text-yellow-400";
                          return <span className={cn("inline-flex px-2 py-1 ml-2 text-xs rounded border", klass)}>{st}</span>;
                        })()}
                        <span className="ml-auto text-xs text-slate-500">{r.reservationTime || '—'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <Dialog open={overrideOpen} onOpenChange={(o) => { setOverrideOpen(o); if (!o) { setOverrideDay(''); setOverrideWindows([]); } }}>
            <DialogContent className="bg-slate-900 border-slate-700">
              <DialogHeader>
                <DialogTitle>Éditer les tranches — {overrideDay}/{String(month).padStart(2,'0')}/{year}</DialogTitle>
                <DialogDescription>Ajoutez des tranches spécifiques pour ce jour en plus des tranches générales.</DialogDescription>
              </DialogHeader>
              <div className="mb-3">
                <div className="text-xs tracking-wide uppercase text-slate-400">Tranches actuelles</div>
                <div className="mt-1">
                  {(slots.days?.[overrideDay]?.windows || []).map((w, i) => (
                    <span key={i} className="inline-block px-2 py-1 mr-1 text-xs rounded border bg-slate-800 border-slate-700">{w.start} - {w.end}</span>
                  ))}
                </div>
              </div>
              <div className="flex justify-end mb-2">
                <Button size="sm" onClick={addOverrideWindow} className="text-white bg-slate-700 hover:bg-slate-600">
                  <Plus size={14} className="mr-1" /> Ajouter une tranche
                </Button>
              </div>
              {overrideWindows.length ? (
                <div className="space-y-2">
                  {overrideWindows.map((w, idx) => (
                    <div key={idx} className="grid grid-cols-3 gap-2">
                      <Input type="time" min="00:00" max="23:59" value={w.start || ''} onChange={(e) => updateOverrideWindow(idx, 'start', e.target.value)} className="text-white bg-slate-900 border-slate-700" placeholder="HH:MM" />
                      <Input type="time" min="00:00" max="23:59" value={w.end || ''} onChange={(e) => updateOverrideWindow(idx, 'end', e.target.value)} className="text-white bg-slate-900 border-slate-700" placeholder="HH:MM" />
                      <Button variant="destructive" size="sm" onClick={() => deleteOverrideWindow(idx)}><Trash2 size={14} /></Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-slate-400">Aucune tranche — ajoutez-en pour ce jour</div>
              )}
              <div className="flex gap-2 justify-end mt-3">
                <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white" onClick={() => { setOverrideOpen(false); setOverrideDay(''); setOverrideWindows([]); }}>Annuler</Button>
                <Button className="text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700" onClick={saveOverride}>Enregistrer</Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={reservationModalOpen} onOpenChange={(o) => { setReservationModalOpen(o); if (!o) { setSelectedReservation(null); } }}>
            <DialogContent className="max-w-lg bg-slate-900 border-slate-700">
              <DialogHeader>
                <DialogTitle>Réservation</DialogTitle>
                <DialogDescription>Détails de la réservation sélectionnée</DialogDescription>
              </DialogHeader>
              {selectedReservation && (
                <div className="space-y-3">
                  <div className="flex gap-2 items-center">
                    <span className="font-semibold text-white">{selectedReservation.name}</span>
                    {(() => {
                      const st = selectedReservation.status || 'pending';
                      const klass = st === 'confirmed'
                        ? "bg-green-500/10 border-green-500/40 text-green-400"
                        : st === 'cancelled'
                          ? "bg-red-500/10 border-red-500/40 text-red-400"
                          : "bg-yellow-500/10 border-yellow-500/40 text-yellow-400";
                      return <span className={cn("inline-flex px-2 py-1 ml-2 text-xs rounded border", klass)}>{st}</span>;
                    })()}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs tracking-wide uppercase text-slate-400">Email</div>
                      <a href={`mailto:${selectedReservation.email || ''}`} className="text-white hover:underline">{selectedReservation.email || '—'}</a>
                    </div>
                    <div>
                      <div className="text-xs tracking-wide uppercase text-slate-400">Téléphone</div>
                      <a href={`tel:${selectedReservation.phone || ''}`} className="text-white hover:underline">{selectedReservation.phone || '—'}</a>
                    </div>
                    <div>
                      <div className="text-xs tracking-wide uppercase text-slate-400">Personnes</div>
                      <div className="text-white">{selectedReservation.guests ?? '—'}</div>
                    </div>
                    <div>
                      <div className="text-xs tracking-wide uppercase text-slate-400">Type</div>
                      <div className="text-white">{selectedReservation.reservationType || '—'}</div>
                    </div>
                    <div>
                      <div className="text-xs tracking-wide uppercase text-slate-400">Date</div>
                      <div className="text-white">{selectedReservation.reservationDate || '—'}</div>
                    </div>
                    <div>
                      <div className="text-xs tracking-wide uppercase text-slate-400">Heure</div>
                      <div className="text-white">{selectedReservation.reservationTime || '—'}</div>
                    </div>
                    <div>
                      <div className="text-xs tracking-wide uppercase text-slate-400">Créée</div>
                      <div className="text-white">{selectedReservation.createdAt || '—'}</div>
                    </div>
                    <div>
                      <div className="text-xs tracking-wide uppercase text-slate-400">Modifiée</div>
                      <div className="text-white">{selectedReservation.updatedAt || '—'}</div>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs tracking-wide uppercase text-slate-400">Menu</div>
                    <div className="mt-1">
                      {Array.isArray(selectedReservation.menuSelections) && selectedReservation.menuSelections.length ? (
                        selectedReservation.menuSelections.map((m, i) => (
                          <span key={i} className="inline-block px-2 py-1 mr-1 text-xs rounded border bg-slate-800 border-slate-700">
                            {m.name} × {m.quantity}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-slate-500">—</span>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-between items-center mt-4">
                    <div className="flex gap-2 items-center">
                      <Label className="text-xs text-slate-400">Statut</Label>
                      <Select
                        value={selectedReservation.status || 'pending'}
                        onValueChange={async (v) => {
                          try {
                            await adminAPI.put(`/reservations/${selectedReservation.id}`, { status: v });
                            setSelectedReservation({ ...selectedReservation, status: v });
                            openDayDetail(selectedDayKey);
                            toast({ title: 'Statut mis à jour', description: v });
                          } catch {
                            toast({ title: 'Erreur de mise à jour', description: 'Impossible de modifier le statut', variant: 'destructive' });
                          }
                        }}
                      >
                        <SelectTrigger className="w-[160px] text-white bg-slate-900 border-slate-700">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-700">
                          <SelectItem value="pending" className="text-white">En attente</SelectItem>
                          <SelectItem value="confirmed" className="text-white">Confirmée</SelectItem>
                          <SelectItem value="cancelled" className="text-white">Annulée</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="destructive"
                        onClick={async () => {
                          if (!window.confirm('Supprimer cette réservation ?')) return;
                          try {
                            await adminAPI.delete(`/reservations/${selectedReservation.id}`);
                            setReservationModalOpen(false);
                            openDayDetail(selectedDayKey);
                            toast({ title: 'Réservation supprimée', description: selectedReservation.name });
                          } catch {
                            toast({ title: 'Erreur de suppression', description: 'Impossible de supprimer', variant: 'destructive' });
                          }
                        }}
                      >
                        Supprimer
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ModernCalendarTab;
